import React, { useEffect, useState } from "react";
import api from "../../api";
import { toast } from "sonner";
import { ChatCircleDots, Warning, CheckCircle, CircleNotch, ArrowSquareOut } from "@phosphor-icons/react";

/**
 * Portal Access toggle — lives inside the Customer edit modal, right below
 * the Billing (Zoho) panel. Flips the customer's `portal_access.status`
 * between `active` and `disabled`.
 *
 * OFF by default for new customers. The Portal calls
 * GET /api/integrations/customer-portal/access-check on every sign-in;
 * this toggle is the only thing that decides authorized (200) vs not (404).
 */
export default function PortalAccessPanel({ customerId }) {
  const [state, setState] = useState(null); // {enabled, provisioned, blockers, ...}
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/customers/${customerId}/portal-access`);
      setState(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load Portal Access");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-line */ }, [customerId]);

  async function toggle(next) {
    setSaving(true);
    try {
      await api.put(`/customers/${customerId}/portal-access`, { enabled: next });
      toast.success(next ? "Portal Access enabled" : "Portal Access disabled");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update Portal Access");
    } finally { setSaving(false); }
  }

  if (loading) {
    return (
      <div className="border border-[#012340]/10 rounded-sm px-4 py-3 text-xs text-[#5C738A]" data-testid="portal-access-loading">
        <CircleNotch size={12} className="animate-spin inline mr-2" /> Loading Portal Access…
      </div>
    );
  }
  if (!state) return null;

  const canFlip = state.enabled ? true : state.can_enable;
  const disabledReason = state.enabled ? null : (state.blockers || []).join(" · ");

  return (
    <div
      className={`border rounded-sm px-4 py-3 ${state.enabled ? "border-[#2E7D32]/40 bg-[#F1F8F2]" : "border-[#012340]/10 bg-white"}`}
      data-testid="portal-access-panel"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] text-[#5C738A]">
            <ChatCircleDots size={12} weight="bold" /> Customer Portal Access
            <a href="https://portal.zmexdisposal.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#CFAB7A] hover:underline">
              portal.zmexdisposal.com <ArrowSquareOut size={10} weight="bold" />
            </a>
          </div>
          <div className="mt-1 text-sm text-[#012340]">
            {state.enabled ? (
              <span className="inline-flex items-center gap-1.5 font-bold text-[#1B5E20]">
                <CheckCircle size={14} weight="fill" /> Enabled — can log in to the Customer Portal
              </span>
            ) : (
              <span className="text-[#5C738A]">
                Disabled — sign-in returns the neutral "if the contact exists we'll send a code" message.
              </span>
            )}
          </div>
          {!state.enabled && state.blockers?.length > 0 && (
            <ul className="mt-2 space-y-1" data-testid="portal-access-blockers">
              {state.blockers.map((b) => (
                <li key={b} className="flex items-center gap-1.5 text-[11px] text-[#B71C1C]">
                  <Warning size={11} weight="fill" /> {b}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Toggle switch */}
        <button
          type="button"
          role="switch"
          aria-checked={state.enabled}
          disabled={!canFlip || saving}
          onClick={() => toggle(!state.enabled)}
          title={disabledReason || (state.enabled ? "Turn OFF" : "Turn ON")}
          className={`
            relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors
            ${state.enabled ? "bg-[#2E7D32]" : "bg-[#012340]/25"}
            ${(!canFlip || saving) ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
          `}
          data-testid="portal-access-toggle"
        >
          <span className={`
            inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow
            ${state.enabled ? "translate-x-6" : "translate-x-1"}
          `} />
        </button>
      </div>

      {state.enabled && (
        <div className="mt-2 pt-2 border-t border-[#012340]/10 text-[11px] text-[#5C738A] flex flex-wrap gap-x-3 gap-y-1">
          {state.email && <span data-testid="portal-access-email"><b className="text-[#012340]">Email:</b> {state.email}</span>}
          {state.phone && <span data-testid="portal-access-phone"><b className="text-[#012340]">Phone:</b> {state.phone}</span>}
          {state.zoho_customer_id && <span><b className="text-[#012340]">Zoho:</b> {state.zoho_customer_id}</span>}
        </div>
      )}
    </div>
  );
}
